import React, { useState, useEffect } from 'react';
import { AiOutlinePlus } from 'react-icons/ai';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { request, BASE_URL } from '../../../api/request.tsx';
import { store } from '../../../store/store.tsx';
import { Select } from 'antd';

interface LanguageData {
  language: string;
  title: string;
  content: string;
  _id?: string; 
}



const CreateNews = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { mode = 'add', newsId, initialData } = location.state || {};
  const [formData, setFormData] = useState({
    type: 'NEWS',
    thumbnail: null as File | null,
    banner: null as File | null,
    priority: '1',
    startDate: new Date().toISOString().slice(0, 16),
    endDate: new Date().toISOString().slice(0, 16),
    status: 'active'
  });

  const [languages, setLanguages] = useState<LanguageData[]>([
    { language: 'Vietnamese', title: '', content: '' }
  ]);

  const [loading, setLoading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');

  useEffect(() => {
    if (mode === 'update' && initialData) {
      setFormData({
        type: initialData.type || '',
        thumbnail: null, 
        banner: null,   
        priority: initialData.priority?.toString() || '1',
        startDate: initialData.startDate ? new Date(initialData.startDate).toISOString().slice(0, 16) : new Date().toISOString().slice(0, 16),
        endDate: initialData.endDate ? new Date(initialData.endDate).toISOString().slice(0, 16) : new Date().toISOString().slice(0, 16),
        status: initialData.isActive ? 'active' : 'inactive'
      });

      // Populate translations
      if (initialData.translations && initialData.translations.length > 0) {
        const populatedLanguages = initialData.translations.map((trans: any) => ({
          language: trans.language === 'vi' ? 'Vietnamese' : trans.language === 'en' ? 'English' : 'Korean',
          title: trans.title || '',
          content: trans.content || '',
          _id: trans._id || undefined 
        }));
        setLanguages(populatedLanguages);
      }
    }
  }, [mode, initialData]);

  const uploadFile = async (file: File) => {
    const formData = new FormData();
    formData.append('image', file); 
    
    try {
      const response = await fetch(`${BASE_URL}upload/upload`, {
        method: 'POST',
        body: formData, 
      });
      
      const resJson = await response.json();
      
      if (resJson?.success) {
        return { data: { url: resJson?.data?.url } };
      } else {
        throw new Error(resJson?.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  };

  const handleInputChange = (field: string, value: string | File | null) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileChange = (field: 'thumbnail' | 'banner', file: File | null) => {
    setFormData(prev => ({
      ...prev,
      [field]: file
    }));
  };

  const handleLanguageChange = (index: number, field: keyof LanguageData, value: string) => {
    const newLanguages = [...languages];
    newLanguages[index] = { ...newLanguages[index], [field]: value };
    setLanguages(newLanguages);
  };

  const addLanguage = () => {
    if (languages.length < 3) {
      setLanguages([...languages, { language: 'Vietnamese', title: '', content: '' }]); // New languages don't have _id
    }
  };

  const removeLanguage = (index: number) => {
    if (languages.length > 1) {
      const newLanguages = languages.filter((_, i) => i !== index);
      setLanguages(newLanguages);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!languages.some(lang => lang.title.trim())) {
      alert('Vui lòng nhập ít nhất một tiêu đề');
      return;
    }

    if (!languages.some(lang => lang.content.trim())) {
      alert('Vui lòng nhập ít nhất một nội dung');
      return;
    }

    setLoading(true);
    setUploadStatus('Đang chuẩn bị...');
    
    try {
      const translations = languages
        .filter(lang => lang.title.trim() && lang.content.trim())
        .map(lang => ({
          language: lang.language.toLowerCase().substring(0, 2), 
          title: lang.title.trim(),
          content: lang.content.trim(),
          ...(mode === 'update' && lang._id ? { _id: lang._id } : {}) 
        }));

      let thumbnailUrl = mode === 'update' && initialData?.thumbnailUrl ? initialData.thumbnailUrl : "";
      let bannerUrl = mode === 'update' && initialData?.bannerUrl ? initialData.bannerUrl : "";

      if (formData.thumbnail) {
        setUploadStatus('Đang upload ảnh thumbnail...');
        
        const thumbnailResponse = await uploadFile(formData.thumbnail);
        // console.log('Thumbnail upload response:', thumbnailResponse);
        
        if (thumbnailResponse?.data?.url) {
          thumbnailUrl = thumbnailResponse.data.url;
        } else {
          console.error('Thumbnail upload failed:', thumbnailResponse);
          alert('Lỗi upload ảnh thumbnail');
          setUploadStatus('');
          return;
        }
      }

      if (formData.banner) {
        setUploadStatus('Đang upload ảnh banner...');
        
        const bannerResponse = await uploadFile(formData.banner);
        // console.log('Banner upload response:', bannerResponse);
        
        if (bannerResponse?.data?.url) {
          bannerUrl = bannerResponse.data.url;
        } else {
          console.error('Banner upload failed:', bannerResponse);
          alert('Lỗi upload ảnh banner');
          setUploadStatus('');
          return;
        }
      }

      const newsData = {
        type: formData.type || "NEWS",
        priority: parseInt(formData.priority),
        startDate: new Date(formData.startDate).toISOString(),
        endDate: new Date(formData.endDate).toISOString(),
        isActive: formData.status === 'active',
        bannerUrl: bannerUrl,
        thumbnailUrl: thumbnailUrl,
        videoUrl: "",
        translations: translations,
        ...(mode === 'add' 
          ? { createdBy: (store.getState().auth.userInfo as any)?.id || "" }
          : { id: newsId }
        )
      };
      
      setUploadStatus(mode === 'add' ? 'Đang tạo bài viết...' : 'Đang cập nhật bài viết...');
      // console.log('Sending news data:', newsData);
      const endpoint = mode === 'add' ? 'news' : `news`;
      const response = await request(endpoint, newsData, mode === 'add' ? 'POST' : 'PUT');
      
      
      if (response?.success || response?.data) {
        alert(mode === 'add' ? 'Tạo bài viết thành công!' : 'Cập nhật bài viết thành công!');
        navigate('/admin/list-news');
      } else {
        console.error('API Error Response:', response);
        alert(`Có lỗi xảy ra khi ${mode === 'add' ? 'tạo' : 'cập nhật'} bài viết: ${response?.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error creating news:', error);
      alert(`Có lỗi xảy ra khi ${mode === 'add' ? 'tạo' : 'cập nhật'} bài viết`);
    } finally {
      setLoading(false);
      setUploadStatus('');
    }
  };

  const classInput = "w-full border-[1px] border-[pink] rounded-md px-2 py-2 outline-none";
  const classLable = "font-semibold text-[pink]";

  return (
    <div className="w-full">
      <div className="text-2xl text-center text-[pink] font-bold mb-6">
        {mode === 'add' ? 'TẠO MỚI BÀI VIẾT' : 'CẬP NHẬT BÀI VIẾT'}
      </div>

      <form onSubmit={handleSubmit}>
        <div className="mb-6 flex justify-center">
          <Select
            value={formData.type}
            onChange={(value) => handleInputChange('type', value)}
            size="large"
            className="custom-select w-1/3 text-[pink] border-[pink] rounded-md"
          >
            <Select.Option value="NEWS">Thông báo</Select.Option>
            <Select.Option value="GUIDE">Hướng dẫn</Select.Option>
          </Select>
        </div>

        {/* Language Sections */}
        {languages.map((lang, index) => (
          <div key={index} className="mb-8 p-4 border border-gray-200 rounded-lg bg-gray-50">
            <div className="flex items-center justify-between mb-4">
              <div className="text-lg font-bold text-[pink]">
                Ngôn ngữ {index + 1}
              </div>
              {languages.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeLanguage(index)}
                  className="text-red-500 hover:text-red-700 text-sm"
                >
                  Xóa
                </button>
              )}
            </div>

            {/* Language Selection */}
            <div className="mb-4">
              <div className={classLable}>Ngôn ngữ</div>
              <select
                value={lang.language}
                onChange={(e) => handleLanguageChange(index, 'language', e.target.value)}
                className={classInput}
              >
                <option value="Vietnamese">Vietnamese</option>
                <option value="English">English</option>
                <option value="Korean">Korean</option>
              </select>
            </div>

            {/* Title */}
            <div className="mb-4">
              <div className={classLable}>Tiêu đề</div>
              <input
                type="text"
                placeholder={`Tiêu đề bài viết ${index + 1}`}
                value={lang.title}
                onChange={(e) => handleLanguageChange(index, 'title', e.target.value)}
                className={classInput}
              />
            </div>

            {/* Content */}
            <div className="mb-4">
              <div className={classLable}>Nội dung</div>
              <div style={{ height: '300px' }}>
                <ReactQuill
                  theme="snow"
                  value={lang.content}
                  onChange={(value) => handleLanguageChange(index, 'content', value)}
                  placeholder={`Nhập nội dung bài viết ${index + 1}...`}
                  modules={{
                    toolbar: [
                      [{ 'header': [1, 2, false] }],
                      ['bold', 'italic', 'underline', 'strike'],
                      ['blockquote'],
                      [{ 'color': [] }, { 'background': [] }],
                      [{ 'align': [] }],
                      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                      ['link', 'image', 'video'],
                      ['clean']
                    ]
                  }}
                  style={{ height: '250px' }}
                />
              </div>
            </div>
          </div>
        ))}

        {/* Add Language Button */}
        {languages.length < 3 && (
          <div className="mb-6 flex justify-center">
            <button
              type="button"
              onClick={addLanguage}
              className="flex items-center gap-2 px-6 py-3 bg-[pink] text-white rounded-md hover:bg-pink-600 transition-colors"
            >
              <AiOutlinePlus className="h-5 w-5" />
              <span className="text-lg">Thêm ngôn ngữ</span>
            </button>
          </div>
        )}

        {/* Thumbnail Image */}
        <div className="mb-3 w-1/2">
          <div className={classLable}>Ảnh thumbnail</div>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleFileChange('thumbnail', e.target.files?.[0] || null)}
            className={classInput}
          />
          {formData.thumbnail && (
            <p className="text-sm text-gray-600 mt-1">
              Đã chọn: {formData.thumbnail.name}
            </p>
          )}
          
          {/* Thumbnail Preview */}
          {(formData.thumbnail || (mode === 'update' && initialData?.thumbnailUrl)) && (
            <div className="mt-3">
              <div className="w-32 h-32 border-2 border-gray-200 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                <img
                  src={
                    formData.thumbnail 
                      ? URL.createObjectURL(formData.thumbnail)
                      : initialData?.thumbnailUrl || ''
                  }
                  alt="Thumbnail preview"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.currentTarget as HTMLImageElement;
                    target.style.display = 'none';
                    const fallback = target.nextElementSibling as HTMLElement;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
                <div className="hidden w-full h-full items-center justify-center">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Banner Image */}
        <div className="mb-3 w-1/2">
          <div className={classLable}>Ảnh banner</div>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleFileChange('banner', e.target.files?.[0] || null)}
            className={classInput}
          />
          {formData.banner && (
            <p className="text-sm text-gray-600 mt-1">
              Đã chọn: {formData.banner.name}
            </p>
          )}
          
          {/* Banner Preview */}
          {(formData.banner || (mode === 'update' && initialData?.bannerUrl)) && (
            <div className="mt-3">
              <div className="w-48 h-32 border-2 border-gray-200 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                <img
                  src={
                    formData.banner 
                      ? URL.createObjectURL(formData.banner)
                      : initialData?.bannerUrl || ''
                  }
                  alt="Banner preview"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.currentTarget as HTMLImageElement;
                    target.style.display = 'none';
                    const fallback = target.nextElementSibling as HTMLElement;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
                <div className="hidden w-full h-full items-center justify-center">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Priority */}
        <div className="mb-3 w-1/2">
          <div className={classLable}>Ưu tiên</div>
          <input
            type="number"
            value={formData.priority}
            onChange={(e) => handleInputChange('priority', e.target.value)}
            min="1"
            className={classInput}
          />
        </div>

        {/* Start Date */}
        <div className="mb-3 w-1/2">
          <div className={classLable}>Ngày bắt đầu</div>
          <input
            type="datetime-local"
            value={formData.startDate}
            onChange={(e) => handleInputChange('startDate', e.target.value)}
            className={classInput}
          />
        </div>

        {/* End Date */}
        <div className="mb-3 w-1/2">
          <div className={classLable}>Ngày kết thúc</div>
          <input
            type="datetime-local"
            value={formData.endDate}
            onChange={(e) => handleInputChange('endDate', e.target.value)}
            className={classInput}
          />
        </div>

        {/* Status */}
        <div className="mb-3 w-1/2">
          <div className={classLable}>Trạng thái</div>
          <select
            value={formData.status}
            onChange={(e) => handleInputChange('status', e.target.value)}
            className={classInput}
          >
            <option value="active">Kích hoạt</option>
            <option value="inactive">Không kích hoạt</option>
          </select>
        </div>

        {/* Submit Button */}
        <div className="flex justify-center flex-col items-center">
          {loading && uploadStatus && (
            <p className="text-[pink] mb-2 text-sm font-medium">
              {uploadStatus}
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            className="px-8 bg-[pink] text-white mb-20 py-2 rounded-md self-center hover:bg-pink-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (uploadStatus || (mode === 'add' ? 'Đang tạo...' : 'Đang cập nhật...')) : (mode === 'add' ? 'Tạo mới' : 'Cập nhật')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateNews;
